import { useState, useEffect, useRef } from 'react';
import axios from './utils/axiosConfig';
import { Table, Form, Card, Badge, Row, Col } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useMediaQuery } from 'react-responsive';

const ExperienceTable = () => {
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'p_id', direction: 'DESC' });
  const { user } = useAuth();
  const observer = useRef();
  const lastExperienceRef = useRef();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isSelf = queryParams.get('self') === 'true';

  // 響應式判斷：1200px 以下顯示手機版卡片視圖（對應 lg 斷點）
  const isMobile = useMediaQuery({ maxWidth: 1200 });

  useEffect(() => {
    fetchExperiences(page, sortConfig.key, sortConfig.direction, filter);
  }, [page, filter, sortConfig, isSelf]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setExperiences([]);
    setPage(1);
    fetchExperiences(1, key, direction, filter);
  };

  const fetchExperiences = async (page, sortKey = 'p_id', sortDirection = 'asc', filter = '') => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/experience', {
        params: { filter, page, sortKey, sortDirection, limit: 10, self: isSelf, eid: user?.e_id },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setExperiences((prevExperiences) => [...prevExperiences, ...response.data.experiences]);
      setHasMore(response.data.experiences.length === 10);
    } catch (error) {
      console.error('Error fetching experiences:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        setPage((prevPage) => prevPage + 1);
      }
    });

    if (lastExperienceRef.current) {
      observer.current.observe(lastExperienceRef.current);
    }

    return () => {
      if (observer.current) observer.current.disconnect();
    };
  }, [loading, hasMore]);

  // 手機版卡片視圖組件
  const MobileCardView = () => (
    <div className="row g-3">
      {experiences.map((experience, index) => (
        <div
          key={experience.p_id}
          className="col-12"
          ref={index === experiences.length - 1 ? lastExperienceRef : null}
        >
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <Badge bg="primary" className="me-2">#{experience.p_id}</Badge>
                  <Badge bg="secondary">{experience.StoreName}</Badge>
                </div>
                <Badge bg="info">{experience.p_arriveat}屆</Badge>
              </div>

              <Card.Title className="h5 mb-3">{experience.EmployeeName}</Card.Title>

              <Row className="g-2">
                <Col xs={6}>
                  <div className="text-muted small">職位</div>
                  <div className="fw-bold">{experience.PositionName || '-'}</div>
                </Col>
                <Col xs={6}>
                  <div className="text-muted small">組別</div>
                  <div className="fw-bold">{experience.p_set || '-'}</div>
                </Col>
                <Col xs={6}>
                  <div className="text-muted small">組別職稱</div>
                  <div className="fw-bold">{experience.p_set_position || '-'}</div>
                </Col>
                <Col xs={6}>
                  <div className="text-muted small">歸檔日期</div>
                  <div className="fw-bold">{experience.p_edit_day || '-'}</div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </div>
      ))}
      {loading && (
        <div className="col-12">
          <Card className="bg-light">
            <Card.Body className="text-center py-4">
              <div className="spinner-border text-primary me-2" role="status">
                <span className="visually-hidden">載入中...</span>
              </div>
              <span>載入中...</span>
            </Card.Body>
          </Card>
        </div>
      )}
    </div>
  );

  // 桌面版表格視圖組件
  const DesktopTableView = () => (
    <div className="table-responsive">
      <Table striped bordered hover variant="dark" className="table-striped table-dark">
        <thead>
          <tr>
            <th onClick={() => handleSort('p_id')} style={{ cursor: 'pointer' }}>
              編號 {sortConfig.key === 'p_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('StoreName')} style={{ cursor: 'pointer' }}>
              社別名稱 {sortConfig.key === 'StoreName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('p_edit_day')} style={{ cursor: 'pointer' }}>
              歸檔日期 {sortConfig.key === 'p_edit_day' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('p_arriveat')} style={{ cursor: 'pointer' }}>
              屆別 {sortConfig.key === 'p_arriveat' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('PositionName')} style={{ cursor: 'pointer' }}>
              職位名稱 {sortConfig.key === 'PositionName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('EmployeeName')} style={{ cursor: 'pointer' }}>
              姓名 {sortConfig.key === 'EmployeeName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('p_set')} style={{ cursor: 'pointer' }}>
              組別 {sortConfig.key === 'p_set' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('p_set_position')} style={{ cursor: 'pointer' }}>
              組別職稱 {sortConfig.key === 'p_set_position' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
          </tr>
        </thead>
        <tbody>
          {experiences.map((experience, index) => (
            <tr
              ref={index === experiences.length - 1 ? lastExperienceRef : null}
              key={experience.p_id}
            >
              <td>{experience.p_id}</td>
              <td>{experience.StoreName}</td>
              <td>{experience.p_edit_day}</td>
              <td>{experience.p_arriveat}</td>
              <td>{experience.PositionName}</td>
              <td>{experience.EmployeeName}</td>
              <td>{experience.p_set}</td>
              <td>{experience.p_set_position}</td>
            </tr>
          ))}
          {loading && (
            <tr>
              <td colSpan="8" className="text-center py-4">
                <div className="spinner-border text-light me-2" role="status">
                  <span className="visually-hidden">載入中...</span>
                </div>
                <span>載入中...</span>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );

  return (
    <div className="container-fluid mt-4">
      {/* 搜尋框 - 響應式調整 */}
      <div className="mb-4">
        <Form.Control
          type="text"
          placeholder="搜尋經歷資料..."
          value={filter}
          onChange={(e) => {
            setExperiences([]);
            setPage(1);
            setFilter(e.target.value);
          }}
          className={isMobile ? 'form-control-lg' : ''}
          style={{ maxWidth: isMobile ? '100%' : '400px' }}
        />
      </div>

      {/* 響應式視圖切換 */}
      {isMobile ? <MobileCardView /> : <DesktopTableView />}

      {/* 無更多數據提示 */}
      {!hasMore && !loading && experiences.length > 0 && (
        <div className="text-center mt-4">
          <Card className="bg-light">
            <Card.Body>
              <p className="mb-0 text-muted">
                {isMobile ? '📋 ' : ''}已顯示全部 {experiences.length} 筆資料
              </p>
            </Card.Body>
          </Card>
        </div>
      )}

      {/* 空狀態提示 */}
      {!loading && experiences.length === 0 && (
        <div className="text-center mt-5">
          <Card className="bg-light">
            <Card.Body className="py-5">
              <div className="display-1 mb-3">📝</div>
              <h5 className="text-muted">查無經歷資料</h5>
              <p className="text-muted small">請嘗試調整搜尋條件</p>
            </Card.Body>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ExperienceTable;
